import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { cn, LinkedInIcon } from '@duck/ui'
import { useTar } from '@/modules/i18n/useTar'
import { trackMarketing } from '@/modules/marketingAnalytics'
import {
  TESTIMONIALS,
  TESTIMONIAL_CATEGORIES,
  type Testimonial,
  type TestimonialCategory,
} from './testimonials.data'

const EXPAND_TRANSITION_MS = 350
const ARROW_SCROLL_RATIO = 0.8

type RowItem =
  | { kind: 'header'; cat: TestimonialCategory; isFirstHeader: boolean; key: string }
  | {
      kind: 'card'
      testimonial: Testimonial
      isFirstInCategory: boolean
      key: string
    }

const buildRow = (): RowItem[] => {
  const rows: RowItem[] = []
  TESTIMONIAL_CATEGORIES.forEach((cat, catIdx) => {
    rows.push({
      kind: 'header',
      cat,
      isFirstHeader: catIdx === 0,
      key: `header-${cat}`,
    })
    const items = TESTIMONIALS.filter((t) => t.category === cat)
    items.forEach((testimonial, idx) => {
      rows.push({
        kind: 'card',
        testimonial,
        isFirstInCategory: idx === 0,
        key: `card-${testimonial.id}`,
      })
    })
  })
  return rows
}

export const Testimonials = () => {
  const { t } = useTar()

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] =
    useState<TestimonialCategory>('users')
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const [naturalHeight, setNaturalHeight] = useState(0)

  const renderRow = useMemo(buildRow, [])

  const expandedTestimonial = useMemo(
    () => TESTIMONIALS.find((t) => t.id === expandedId) ?? null,
    [expandedId]
  )

  // Track scroll edges + active category
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handler = () => {
      const x = el.scrollLeft
      const max = el.scrollWidth - el.clientWidth
      setCanScrollLeft(x > 1)
      setCanScrollRight(x < max - 1)

      const headers = Array.from(
        el.querySelectorAll<HTMLElement>('[data-cat-header]')
      )
      let current: TestimonialCategory = 'users'
      for (const header of headers) {
        if (header.offsetLeft <= x + 1) {
          current = header.dataset.catHeader as TestimonialCategory
        }
      }
      setActiveCategory(current)
    }
    el.addEventListener('scroll', handler, { passive: true })
    handler()
    const ro = new ResizeObserver(handler)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', handler)
      ro.disconnect()
    }
  }, [])

  const scrollByAmount = useCallback((dir: 1 | -1) => {
    const el = containerRef.current
    if (!el) return
    trackMarketing('home:testimonials:scroll_arrow_click', {
      direction: dir === 1 ? 'right' : 'left',
    })
    el.scrollBy({
      left: dir * el.clientWidth * ARROW_SCROLL_RATIO,
      behavior: 'smooth',
    })
  }, [])

  const scrollToCategory = useCallback((cat: TestimonialCategory) => {
    const el = containerRef.current
    if (!el) return
    trackMarketing('home:testimonials:category_click', { category: cat })
    const target = el.querySelector<HTMLElement>(`[data-cat-header="${cat}"]`)
    if (!target) return
    el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' })
  }, [])

  const handleExpand = useCallback((testimonial: Testimonial) => {
    trackMarketing('home:testimonials:card_expand', {
      id: testimonial.id,
      category: testimonial.category,
    })
    setExpandedId(testimonial.id)
  }, [])

  const handleCollapse = useCallback((id: string) => {
    trackMarketing('home:testimonials:card_collapse', { id })
    setExpandedId(null)
  }, [])

  // Measure marquee row height once it's laid out, then keep it fresh
  useEffect(() => {
    if (!rowRef.current) return
    const update = () => {
      if (rowRef.current) setNaturalHeight(rowRef.current.offsetHeight)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(rowRef.current)
    return () => ro.disconnect()
  }, [])

  return (
    <section
      className="space-y-6"
      aria-labelledby="testimonials-heading"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 border-b border-border pb-3">
        <h2
          id="testimonials-heading"
          className="text-xs uppercase tracking-[0.25em] text-muted-foreground"
        >
          {/*<span className="tabular-nums">03</span>*/}
          {/*<span aria-hidden className="mx-2 text-muted-foreground/50">/</span>*/}
          {t('home.kickers.voices')}
        </h2>
        <div className="flex items-baseline gap-2 text-sm">
          {TESTIMONIAL_CATEGORIES.map((cat, idx) => (
            <span key={cat} className="flex items-baseline gap-2">
              {idx > 0 && (
                <span aria-hidden className="text-muted-foreground/50">
                  ·
                </span>
              )}
              <button
                type="button"
                onClick={() => scrollToCategory(cat)}
                className={cn(
                  'transition-colors',
                  activeCategory === cat
                    ? 'border-b border-foreground pb-0.5 font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                aria-pressed={activeCategory === cat}
              >
                {t(`home.testimonials.categories.${cat}`)}
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div
          style={{
            paddingBottom:
              expandedTestimonial && !expandedTestimonial.isShort
                ? naturalHeight
                : 0,
            transition: `padding-bottom ${EXPAND_TRANSITION_MS}ms ease-out`,
          }}
        >
          <div
            ref={containerRef}
            dir="ltr"
            className="overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            aria-hidden={!!expandedTestimonial}
          >
            <div ref={rowRef} className="flex items-stretch gap-4 py-2">
              {renderRow.map((item) =>
                item.kind === 'header' ? (
                  <CategoryDivider
                    key={item.key}
                    cat={item.cat}
                    isFirstHeader={item.isFirstHeader}
                  />
                ) : (
                  <div
                    key={item.key}
                    data-cat-first={
                      item.isFirstInCategory
                        ? item.testimonial.category
                        : undefined
                    }
                    className="w-72 shrink-0 md:w-80"
                  >
                    <TestimonialCard
                      testimonial={item.testimonial}
                      onExpand={() => handleExpand(item.testimonial)}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {!expandedTestimonial && (
          <>
            <ScrollArrow
              direction="left"
              visible={canScrollLeft}
              onClick={() => scrollByAmount(-1)}
            />
            <ScrollArrow
              direction="right"
              visible={canScrollRight}
              onClick={() => scrollByAmount(1)}
            />
          </>
        )}

        {expandedTestimonial && (
          <ExpandedView
            testimonial={expandedTestimonial}
            onClose={() => handleCollapse(expandedTestimonial.id)}
          />
        )}
      </div>
    </section>
  )
}

type ScrollArrowProps = {
  direction: 'left' | 'right'
  visible: boolean
  onClick: () => void
}

const ScrollArrow = ({ direction, visible, onClick }: ScrollArrowProps) => {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Scroll ${direction}`}
      tabIndex={visible ? 0 : -1}
      className={cn(
        'absolute top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-background/85 p-2 text-foreground shadow-sm backdrop-blur-sm transition-opacity hover:bg-accent/10',
        direction === 'left' ? 'left-2' : 'right-2',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0'
      )}
    >
      <Icon className="h-5 w-5" />
    </button>
  )
}

const CategoryDivider = ({
  cat,
  isFirstHeader,
}: {
  cat: TestimonialCategory
  isFirstHeader: boolean
}) => (
  <div
    data-cat-header={cat}
    className="flex shrink-0 items-center px-2"
    aria-hidden
  >
    {!isFirstHeader && <div className="h-20 w-px bg-border" />}
  </div>
)

type TestimonialCardProps = {
  testimonial: Testimonial
  onExpand: () => void
}

const TestimonialCard = ({ testimonial, onExpand }: TestimonialCardProps) => {
  const { t } = useTar()
  return (
    <article
      className="relative h-full overflow-hidden rounded-lg border border-border bg-card p-5 text-start transition-shadow hover:shadow-sm"
      dir="ltr"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute left-4 top-2 select-none font-serif text-5xl leading-none text-accent/15"
      >
        &ldquo;
      </span>

      <header className="relative mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-base font-semibold text-foreground">
            {testimonial.name}
          </span>
          {testimonial.linkedinUrl && (
            <a
              href={testimonial.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${testimonial.name} on LinkedIn`}
              onClick={() =>
                trackMarketing('home:testimonials:linkedin_click', {
                  id: testimonial.id,
                  surface: 'card',
                })
              }
              className="text-muted-foreground transition-colors hover:text-accent"
            >
              <LinkedInIcon size={14} />
            </a>
          )}
        </div>
        {testimonial.role && (
          <div className="text-xs italic text-muted-foreground">
            {testimonial.role}
          </div>
        )}
      </header>

      <button
        type="button"
        onClick={onExpand}
        className="group relative flex w-full flex-col items-start text-start"
      >
        <p className="line-clamp-5 font-serif text-sm italic leading-relaxed text-foreground">
          {testimonial.summary}
        </p>
        <span className="mt-3 text-xs font-medium text-accent transition-opacity group-hover:opacity-80">
          {t('home.testimonials.readFull')} →
        </span>
      </button>
    </article>
  )
}

type ExpandedViewProps = {
  testimonial: Testimonial
  onClose: () => void
}

const ExpandedView = ({ testimonial, onClose }: ExpandedViewProps) => {
  const { t } = useTar()
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={`testimonial-${testimonial.id}-name`}
      className="absolute inset-0 flex animate-in fade-in zoom-in-95"
      style={{ animationDuration: `${EXPAND_TRANSITION_MS}ms` }}
      dir="ltr"
    >
      <article className="relative flex h-full w-full flex-col items-center overflow-hidden rounded-lg border border-border bg-card p-8 shadow-md">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('home.testimonials.close')}
          className="absolute end-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent/10 hover:text-foreground"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>

        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 select-none font-serif text-7xl leading-none text-accent/15"
        >
          &ldquo;
        </span>

        <header className="relative mb-5 w-full max-w-2xl">
          <div className="flex items-center gap-2">
            <span
              id={`testimonial-${testimonial.id}-name`}
              className="text-lg font-semibold text-foreground"
            >
              {testimonial.name}
            </span>
            {testimonial.linkedinUrl && (
              <a
                href={testimonial.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${testimonial.name} on LinkedIn`}
                onClick={() =>
                  trackMarketing('home:testimonials:linkedin_click', {
                    id: testimonial.id,
                    surface: 'expanded',
                  })
                }
                className="text-muted-foreground transition-colors hover:text-accent"
              >
                <LinkedInIcon size={16} />
              </a>
            )}
          </div>
          {testimonial.role && (
            <div className="text-sm italic text-muted-foreground">
              {testimonial.role}
            </div>
          )}
        </header>

        <div className="relative min-h-0 w-full max-w-2xl flex-1 space-y-4 overflow-y-auto px-2 font-serif text-[15px] leading-relaxed text-foreground">
          {testimonial.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </article>
    </div>
  )
}
